import { isBlockedHostname } from './blockedHost';
import {
    CLARIFY_ITEM_ID_PATTERN,
    type FieldErrorCode,
    INTEGER_ITEM_PATTERN,
} from './fieldErrorCodes';
import { validateScadaItems } from './scadaItems';
import {
    type FieldValue,
    type ProviderFieldKind,
    type ProviderFieldSchema,
    type ProviderSchema,
    SECRET_NAMES,
    type SecretName,
} from './types';

/**
 * Client mirror of the server's field rules (field-rules.ts). It exists for fast feedback;
 * the server stays the authority, so nothing here may refuse a value the server accepts.
 */
export const FIELD_TEXT_MAX = 255;
/** FieldPatchDto refuses a longer list with a bare 400, so the form must stop it first. */
export const LIST_MAX_ITEMS = 200;

/** A bare host: DNS labels only, so a scheme, port or path cannot reach the TDS connect. */
const HOST_LABEL = '[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?';
const HOST_RE = new RegExp(`^${HOST_LABEL}(?:\\.${HOST_LABEL})*$`);
const SQL_IDENTIFIER_RE = /^[A-Za-z_][A-Za-z0-9_]{0,127}$/;
const SQL_QUALIFIED_NAME_RE = /^[A-Za-z_][A-Za-z0-9_]{0,127}(\.[A-Za-z_][A-Za-z0-9_]{0,127}){0,2}$/;
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Secret fields are keyed by their column; the API names them by SecretName. */
export const SECRET_COLUMN: Readonly<Record<SecretName, string>> = {
    apiKey: 'client_key',
    clientId: 'client_id',
    clientSecret: 'client_secret',
};

/** The secrets a provider takes, by the name the API uses for them. */
export function secretNamesOf(schema: ProviderSchema): SecretName[] {
    return SECRET_NAMES.filter((name) =>
        schema.fields.some((field) => field.kind === 'secret' && field.key === SECRET_COLUMN[name])
    );
}

type ListKind = 'integerList' | 'stringList';
type TextKind = Exclude<ProviderFieldKind, ListKind>;

export function isListKind(kind: ProviderFieldKind): kind is ListKind {
    return kind === 'integerList' || kind === 'stringList';
}

/** The code a value of the wrong shape (a list for text, text for a list) is reported with. */
const SHAPE_CODE: Readonly<Record<ProviderFieldKind, FieldErrorCode>> = {
    url: 'httpsUrl',
    host: 'host',
    text: 'tooLong',
    integer: 'integer',
    secret: 'unknownField',
    integerList: 'listItem',
    stringList: 'listItem',
    identifier: 'sqlIdentifier',
    qualifiedName: 'sqlQualifiedName',
    clarifyItem: 'clarifyItemId',
    enum: 'enum',
    date: 'invalidDate',
    scadaItems: 'scadaItems',
};

/** Unset for every check: null, blank text, or a list with no non-blank entry. */
export function isBlankValue(value: FieldValue): boolean {
    if (value === null) return true;
    if (typeof value === 'string') return value.trim() === '';
    return value.every((entry) => entry.trim() === '');
}

function isIsoDay(text: string): boolean {
    const date = new Date(`${text}T00:00:00Z`);
    return (
        DAY_RE.test(text) &&
        !Number.isNaN(date.getTime()) &&
        date.toISOString().slice(0, 10) === text
    );
}

/** R19: https only, on the default port, to a host that is not internal by name. */
function urlProblem(text: string): FieldErrorCode | null {
    if (!text.startsWith('https://')) return 'httpsUrl';
    let url: URL;
    try {
        url = new URL(text);
    } catch {
        return 'httpsUrl';
    }
    if (url.protocol !== 'https:' || url.hostname === '') return 'httpsUrl';
    // Credentials in the URL would be stored in the clear and repeated in provider errors.
    if (url.username !== '' || url.password !== '') return 'urlCredentials';
    // The parser drops an explicit :443, so any port left is a non-default one.
    if (url.port !== '' || isBlockedHostname(url.hostname)) return 'blockedHost';
    return null;
}

function hostProblem(text: string): FieldErrorCode | null {
    if (!HOST_RE.test(text)) return 'host';
    return isBlockedHostname(text) ? 'blockedHost' : null;
}

function isIntegerText(field: ProviderFieldSchema, text: string): boolean {
    if (!INTEGER_ITEM_PATTERN.test(text)) return false;
    // BigInt, since eighteen digits do not fit a double exactly.
    return field.max === null || BigInt(text) <= BigInt(field.max);
}

function textProblem(
    field: ProviderFieldSchema,
    kind: TextKind,
    text: string
): FieldErrorCode | null {
    if (kind === 'secret') return 'unknownField';
    if (kind === 'scadaItems') return validateScadaItems(text);
    if (text.length > FIELD_TEXT_MAX) return 'tooLong';
    switch (kind) {
        case 'url':
            return urlProblem(text);
        case 'host':
            return hostProblem(text);
        case 'integer':
            return isIntegerText(field, text) ? null : 'integer';
        case 'clarifyItem':
            return CLARIFY_ITEM_ID_PATTERN.test(text) ? null : 'clarifyItemId';
        case 'identifier':
            return SQL_IDENTIFIER_RE.test(text) ? null : 'sqlIdentifier';
        case 'qualifiedName':
            return SQL_QUALIFIED_NAME_RE.test(text) ? null : 'sqlQualifiedName';
        case 'enum':
            return field.enumValues.includes(text) ? null : 'enum';
        case 'date':
            return isIsoDay(text) ? null : 'invalidDate';
        case 'text':
            return null;
    }
}

function listItemOk(field: ProviderFieldSchema, item: string): boolean {
    if (field.kind === 'integerList') return isIntegerText(field, item);
    return item.length <= FIELD_TEXT_MAX;
}

/**
 * A field a secret is sent to (a URL or the Arnalax host). As on the server, its rules judge
 * every row a save touches or creates, written or not: a secret is never saved toward a
 * destination the rules refuse. Every other format rule judges written values only.
 */
export function isDestinationField(field: ProviderFieldSchema): boolean {
    return field.kind === 'url' || field.kind === 'host';
}

/**
 * The format problem with one value, or null, with the server's codes. Values are checked
 * trimmed; a blank value of the right shape passes, since whether it may be blank is the
 * required rule's call.
 */
export function clientFieldError(field: ProviderFieldSchema, value: FieldValue): string | null {
    if (value === null) return null;
    const kind = field.kind;
    if (isListKind(kind)) {
        if (!Array.isArray(value)) return SHAPE_CODE[kind];
        // The DTO counts the list as sent, blank entries included.
        if (value.length > LIST_MAX_ITEMS) return 'tooMany';
        const items = value.map((entry) => entry.trim()).filter((entry) => entry !== '');
        return items.every((item) => listItemOk(field, item)) ? null : 'listItem';
    }
    if (Array.isArray(value)) return SHAPE_CODE[kind];
    const text = value.trim();
    return text === '' ? null : textProblem(field, kind, text);
}

/** Delta Sharing puts share and schema into the request path unescaped (R29). */
const UNSAFE_SEGMENT_RE = /[/?#%\s]|^\.+$/;
const PATH_SEGMENT_KEYS = new Set(['extra_config.share', 'extra_config.schema']);

/**
 * An account field's problem: the shared format rule first, then the account rules the
 * server adds (account-schemas.ts): path-safe share and schema, known objects only.
 */
export function clientAccountFieldError(
    field: ProviderFieldSchema,
    value: FieldValue
): string | null {
    const format = clientFieldError(field, value);
    if (format !== null) return format;
    if (PATH_SEGMENT_KEYS.has(field.key))
        return typeof value === 'string' && UNSAFE_SEGMENT_RE.test(value.trim())
            ? 'pathSegment'
            : null;
    if (field.key === 'extra_config.objects' && Array.isArray(value)) {
        const items = value.map((item) => item.trim()).filter((item) => item !== '');
        return items.every((item) => field.enumValues.includes(item)) ? null : 'listItem';
    }
    return null;
}
